import classNames from 'classnames'
import _ from 'lodash'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import apis from 'browser/app/models/apis'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { ComponentsContext } from 'browser/contexts/components/components-context'
import { schemaSetsHorizontalLayout } from 'shared-libs/components/ui2/forms/ui2-form/with-ui2-form-context'
import { getWebEntityDisplayFromSchema, getWebEntityIconByName } from 'browser/app/models/entity-display-registry'
import { translateString } from 'shared-libs/helpers/utils'
import {
  UI2EntitySearchFieldProps,
  UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS,
} from 'shared-libs/components/ui2/fields/ui2-entity-search-field/ui2-entity-search-field-props'
import {
  DEFAULT_ENTITY_TYPE_NAME,
  getFindDifferentLabel,
  resolveEntityDisplayNames,
  resolveSchemaPaths,
} from 'shared-libs/components/ui2/fields/ui2-entity-search-field/entity-search-utils'
import { useEntitySearchSelection } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/use-entity-search-selection'
import { useEntitySearchSubmitMode } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/use-entity-search-submit-mode'

import { UI2EntityPreviewCard } from 'browser/components/ui2/display/ui2-entity-preview-card/ui2-entity-preview-card'
import {
  UI2PulseHighlight,
  IUI2PulseHighlightRef,
} from 'browser/components/ui2/behavior/pulse-highlight/pulse-highlight'

import { useUI2Platform } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'

import { UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2EmptyStateLauncher } from '../field-ui/empty-state-launcher/empty-state-launcher'
import { EntitySearchSelectedCard, IEntitySearchSelectedCardRef } from './entity-search-selected-card'
import { EntitySearchSubmitInput } from './entity-search-submit-input'
import { EntitySearchAutoModal } from './entity-search-auto-modal'
import { EntitySearchMultiChips, IEntitySearchMultiChipsHandle, MultiSelectValue } from './entity-search-multi-chips'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_ui2-entity-search-field.scss'

// ─── Defaults (sourced from shared contract) ──────────────────────────────────

const DEFAULT_MIN_QUERY_LENGTH = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.minQueryLength!
const DEFAULT_SEARCH_MODE = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.searchMode!
const DEFAULT_CREATABLE_MODE = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.creatableMode!
const DEFAULT_IS_CLEARABLE = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.isClearable!

// ─── Component ───────────────────────────────────────────────────────────────

interface IUI2EntitySearchFieldWebProps extends UI2EntitySearchFieldProps {
  onChange?: (value: any) => void
  errors?: any[]
}

/**
 * @uiComponent ui2:entitySearchField
 */
export function UI2EntitySearchField(props: IUI2EntitySearchFieldWebProps) {
  const {
    value,
    onChange,
    onEntityChange,
    onSubmitSearch,
    onFiltersChange,
    valueMappings,
    frames,
    query,
    searchMode = DEFAULT_SEARCH_MODE,
    isClearable = DEFAULT_IS_CLEARABLE,
    autoFocus,
    placeholder,
    translationTable,
    isDisabled,
    isReadOnly,
    isMultiSelect,
    minQueryLength = DEFAULT_MIN_QUERY_LENGTH,
    preloadResults,
    confirmOnSelect,
    confirmButtonLabel,
    creatableMode = DEFAULT_CREATABLE_MODE,
    isCreatable,
    rawQueryPath,
    openEntityOnSelect,
    optionValuePath = 'displayName',
    transformSearchFilterFn,
    transformResultsFn,
    // Schema path props
    entitySearchResultCardSchemaPath = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.entitySearchResultCardSchemaPath!,
    searchHelpSchemaPath = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.searchHelpSchemaPath!,
    entityPreviewCardSchemaPath = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.entityPreviewCardSchemaPath!,
    searchEmptyResultsSchemaPath = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.searchEmptyResultsSchemaPath!,
    entityCreateSchemaPath = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.entityCreateSchemaPath!,
    entityEditSchemaPath = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.entityEditSchemaPath!,
    entityReadOnlySchemaPath = UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS.entityReadOnlySchemaPath!,
    extraState,
    // Field shell passthrough
    ...fieldShellPassthrough
  } = props

  const isMultiSelectMode = isMultiSelect === true && searchMode !== 'submit'
  const multiValues: MultiSelectValue[] = isMultiSelectMode && Array.isArray(value) ? value : []
  const isMobileWeb = useUI2Platform() === 'mobileWeb'

  const componentsContext = useContext(ComponentsContext)
  const store = apis.getStore()

  // ─── Entity schema ──────────────────────────────────────────────────────────
  const [entitySchema, setEntitySchema] = useState<any>({})
  const [entityDisplayName, setEntityDisplayName] = useState(DEFAULT_ENTITY_TYPE_NAME)
  const [entityDisplayNamePlural, setEntityDisplayNamePlural] = useState(DEFAULT_ENTITY_TYPE_NAME)

  useEffect(() => {
    const entityType = query?.entityType
    if (!entityType) {
      setEntitySchema({})
      setEntityDisplayName(DEFAULT_ENTITY_TYPE_NAME)
      setEntityDisplayNamePlural(DEFAULT_ENTITY_TYPE_NAME)
      return
    }
    let cancelled = false
    store.resolveSchema(entityType).then((schema: any) => {
      if (cancelled) return
      setEntitySchema(schema ?? {})
      const names = resolveEntityDisplayNames(query, schema, translationTable)
      setEntityDisplayName(names.singular)
      setEntityDisplayNamePlural(names.plural)
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.entityType])

  // ─── isValueLocked (set once at mount) ─────────────────────────────────────
  const isValueLocked = useRef(isClearable === false && !!_.get(value, 'entityId')).current

  // ─── Pulse + scroll refs (triggered on modal close) ─────────────────────────
  const fieldPulseRef = useRef<IUI2PulseHighlightRef>(null)
  const fieldScrollRef = useRef<HTMLDivElement>(null)
  // In multi-select mode, pulse the individual chips instead of the field container.
  const multiChipsRef = useRef<IEntitySearchMultiChipsHandle>(null)
  // On MW single-select, pulse just the card ring (not the entire field body which
  // includes the find-different button below the card). On DW the outer ring is fine.
  const selectedCardRef = useRef<IEntitySearchSelectedCardRef>(null)
  const pulseSelectedCard = () =>
    isMobileWeb ? selectedCardRef.current?.pulse() : fieldPulseRef.current?.pulse()
  // Prevents double-pulse when a selection was just committed (the card's own
  // --pulse animation already fires in that case).
  const selectionJustChangedRef = useRef(false)

  // ─── Selection hook ─────────────────────────────────────────────────────────
  const fetchEntity = useCallback(
    (uniqueId: string) => store.findRecord(uniqueId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const {
    entity,
    isLoading: isEntityLoading,
    pulseTick,
    handleCommitSelection,
    handleClear,
  } = useEntitySearchSelection({
    value,
    valueMappings,
    frames: frames as any,
    onChange,
    onEntityChange,
    rawQueryPath,
    fetchEntity,
  })

  // ─── Submit-mode hook ───────────────────────────────────────────────────────
  const {
    submitSearchFilter,
    submitRequestedSearchFilter,
    isCurrentFilterCommitted,
    isSubmitResultsOpen,
    isSubmitButtonVisible,
    handleSubmitFilterChange,
    handleSubmitPress,
    handleSubmitResultsClose,
    handleSubmitSearch,
    handleCommitRawValue,
    handleEntityCommittedFromResults,
    setIsSubmitResultsOpen,
  } = useEntitySearchSubmitMode({
    minQueryLength,
    onSubmitSearch,
    onChange,
    rawQueryPath,
    frames: frames as any,
  })

  // When an entity is committed from submit-mode results, also mark as committed.
  const handleSubmitCommitSelection = useCallback(
    (payload: { entity: any; searchFilter: string }) => {
      handleCommitSelection(payload)
      handleEntityCommittedFromResults()
    },
    [handleCommitSelection, handleEntityCommittedFromResults]
  )

  // ─── Submit wrapper resize ──────────────────────────────────────────────────
  const [submitWrapperWidth, setSubmitWrapperWidth] = useState(0)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    return () => { resizeObserverRef.current?.disconnect() }
  }, [])

  const handleSubmitWrapperRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect()
    resizeObserverRef.current = null
    if (!node) return
    const observer = new ResizeObserver(() => {
      const w = node.getBoundingClientRect().width
      setSubmitWrapperWidth((prev) => (Math.abs(w - prev) > 0.5 ? w : prev))
    })
    observer.observe(node)
    resizeObserverRef.current = observer
    setSubmitWrapperWidth(node.getBoundingClientRect().width)
  }, [])

  // ─── Auto-open search overlay ───────────────────────────────────────────────
  const hasAutoOpened = useRef(false)
  const searchOverlayRef = useRef<Element | null>(null)

  useEffect(() => {
    if (searchMode === 'auto' && autoFocus && !hasAutoOpened.current) {
      hasAutoOpened.current = true
      openOverlay()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Resolved schema paths ──────────────────────────────────────────────────
  const schemaPaths = useMemo(
    () =>
      resolveSchemaPaths(
        {
          isCreatable,
          entitySearchResultCardSchemaPath,
          searchHelpSchemaPath,
          searchEmptyResultsSchemaPath,
          entityPreviewCardSchemaPath,
          entityCreateSchemaPath,
        },
        entitySchema
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entitySchema]
  )

  // Vertical unless the uiSchema node asks for horizontal. The auto-mode search
  // surface and the selected entity card both want the full row width, and a
  // horizontal form would otherwise squeeze them into its control cell.
  //
  // The old test was `props.isHorizontalLayout !== undefined`, which is never
  // true in a form — the renderer spreads its context object (carrying the
  // form's layout) into every component — so the field silently inherited the
  // form's layout and the intended default never applied. A function component
  // can't express this with `defaultProps` the way fileUploadField does
  // (React 18.3 deprecates that), so it asks the same question directly.
  const isHorizontalLayout =
    schemaSetsHorizontalLayout(props) && !!(fieldShellPassthrough as any)?.isHorizontalLayout

  // ─── Overlay helpers ────────────────────────────────────────────────────────
  function openOverlay({ showSelectedOnly = false }: { showSelectedOnly?: boolean } = {}) {
    selectionJustChangedRef.current = false

    const initialSelectedEntityMap = isMultiSelectMode
      ? new Map(multiValues.map((v) => [v.entityId, { uniqueId: v.entityId, displayName: v.displayName }]))
      : undefined

    searchOverlayRef.current = OverlayManager.openOverlayElement(
      <Modal
        backdropClosesModal={true}
        modalDialogClassName={classNames('ui2-entitySearchAutoModal-dialog', { 'ui2--mobileWeb': isMobileWeb })}
        modalPaperClassName={classNames('ui2-entitySearchAutoModal-paper', { 'ui2--mobileWeb': isMobileWeb })}
        onClose={() => {
          searchOverlayRef.current = null
          fieldScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          if (!selectionJustChangedRef.current) {
            if (isMultiSelectMode) {
              multiChipsRef.current?.pulse()
            } else {
              fieldPulseRef.current?.pulse()
            }
          }
        }}
      >
        <EntitySearchAutoModal
          fieldProps={{
            ...props,
            minQueryLength,
            preloadResults,
            creatableMode,
            confirmOnSelect: isMultiSelectMode ? undefined : confirmOnSelect,
            confirmButtonLabel,
            transformSearchFilterFn,
            transformResultsFn,
          }}
          componentsContext={componentsContext}
          entitySchema={entitySchema}
          entityDisplayName={entityDisplayName}
          entityDisplayNamePlural={entityDisplayNamePlural}
          schemaPaths={schemaPaths}
          initialSelectedEntityMap={initialSelectedEntityMap}
          initialShowSelectedOnly={showSelectedOnly}
          onCommitMultiSelection={isMultiSelectMode ? handleMultiCommit : undefined}
          isMobileWeb={isMobileWeb}
          onCommitSelection={(payload) => {
            if (!isMultiSelectMode) {
              selectionJustChangedRef.current = true
              handleCommitSelection(payload)
              if (openEntityOnSelect && payload.entity) {
                // Pulse is deferred to when the entity form modal closes so
                // the ring fires where the user's attention lands, not while
                // the modal is still covering the field.
                handleOpenEntityAfterSelect(payload.entity, pulseSelectedCard)
              }
            }
          }}
          onCreateEntity={handleCreateEntity}
          onClose={() => {
            searchOverlayRef.current = null
          }}
        />
      </Modal>
    )
  }

  function handleMultiCommit(entities: any[]) {
    const payload = entities.map((e) => ({
      entityId: e?.uniqueId ?? e?.entityId,
      displayName: e?.displayName,
    }))
    onChange?.(payload)
  }

  function handleCreateEntity(searchFilter: string) {
    if (!schemaPaths.entityCreate) return
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { EntityFormModal } = require(
      'browser/components/atomic-elements/organisms/entity/entity-form-modal'
    ) as typeof import('browser/components/atomic-elements/organisms/entity/entity-form-modal')
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { EntityFormSheetMode } = require(
      'browser/components/atomic-elements/organisms/entity/entity-form-sheet'
    ) as typeof import('browser/components/atomic-elements/organisms/entity/entity-form-sheet')
    OverlayManager.openOverlayElement(
      <Modal
        backdropClosesModal={true}
        modalDialogClassName='c-entityFormModal-dialog'
        modalPaperClassName='c-entityFormModal-paper'
      >
        <EntityFormModal
          entitySchema={entitySchema}
          defaultValue={{ searchFilter }}
          uiSchemaPath={schemaPaths.entityCreate}
          mode={EntityFormSheetMode.Create}
          extraState={extraState}
          onCreate={handleCreatedEntityCommit}
          onSelect={handleCreatedEntityCommit}
          onEdit={handleCreatedEntityCommit}
        />
      </Modal>
    )
  }

  function handleOpenEntityAfterSelect(entity: any, onClose?: () => void) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { EntityFormModal } = require(
      'browser/components/atomic-elements/organisms/entity/entity-form-modal'
    ) as typeof import('browser/components/atomic-elements/organisms/entity/entity-form-modal')
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { EntityFormSheetMode } = require(
      'browser/components/atomic-elements/organisms/entity/entity-form-sheet'
    ) as typeof import('browser/components/atomic-elements/organisms/entity/entity-form-sheet')

    const viewerFirmId = apis.getSettings()?.firm?.uniqueId
    const ownerFirmId = _.get(entity, 'owner.firm.entityId')
    const viewerOwnsEntity = !viewerFirmId || !ownerFirmId || viewerFirmId === ownerFirmId

    const wantsEdit =
      openEntityOnSelect === 'edit' || (openEntityOnSelect === 'auto' && viewerOwnsEntity)

    const editResolvable =
      !!entityEditSchemaPath && !!entity?.resolveSubschemaByPath?.(entityEditSchemaPath)
    const readonlyResolvable =
      !!entityReadOnlySchemaPath && !!entity?.resolveSubschemaByPath?.(entityReadOnlySchemaPath)

    let uiSchemaPath: string | undefined
    let mode: typeof EntityFormSheetMode[keyof typeof EntityFormSheetMode]

    if (wantsEdit && editResolvable) {
      uiSchemaPath = entityEditSchemaPath
      mode = EntityFormSheetMode.Edit
    } else if (wantsEdit && readonlyResolvable) {
      // edit wanted but edit schema absent — fall back to read-only
      uiSchemaPath = entityReadOnlySchemaPath
      mode = EntityFormSheetMode.View
    } else if (!wantsEdit && readonlyResolvable) {
      uiSchemaPath = entityReadOnlySchemaPath
      mode = EntityFormSheetMode.View
    } else {
      // eslint-disable-next-line no-console
      console.log(
        '[UI2EntitySearchField] openEntityOnSelect: no entity UI opened — no resolvable schema path found.',
        { openEntityOnSelect, wantsEdit, entityEditSchemaPath, entityReadOnlySchemaPath }
      )
      // No modal will open — fire the deferred pulse immediately.
      onClose?.()
      return
    }

    OverlayManager.openOverlayElement(
      <Modal
        backdropClosesModal={true}
        modalDialogClassName={classNames('c-entityFormModal-dialog', { 'c-entityFormModal-dialog--mobileWeb': isMobileWeb })}
        modalPaperClassName={classNames('c-entityFormModal-paper', { 'c-entityFormModal-paper--mobileWeb': isMobileWeb })}
        onClose={onClose}
      >
        <EntityFormModal
          entity={entity}
          entitySchema={entity?.schema}
          uiSchemaPath={uiSchemaPath}
          mode={mode}
          isMobileWeb={isMobileWeb}
          extraState={extraState}
          // This sheet is the confirmation step of a selection, so it offers
          // Back / Continue rather than Cancel / Save. The preview card opens
          // the same component WITHOUT this — there the user is inspecting a
          // value they already own and there is no search to go back to.
          selectionFlow={{
            onBack: () => openOverlay(),
            // MW gets the short label: the footer holds two pills side by side
            // and there is no width for `Find a different trailer` — it
            // ellipsizes down to `FIND …`, which says nothing. NM defaults to
            // `Back` for the same reason. DW has the room for the descriptive
            // form, and it disambiguates there from the modal's own close.
            backText: isMobileWeb ? 'Back' : getFindDifferentLabel(entityDisplayName),
          }}
          className={isMobileWeb ? 'c-entityFormModal--mobileWeb' : undefined}
        />
      </Modal>
    )
  }

  function handleCreatedEntityCommit(newEntity: any) {
    if (!newEntity) return
    handleCommitSelection({ entity: newEntity, searchFilter: '' })
    if (searchOverlayRef.current) {
      OverlayManager.closeOverlay(searchOverlayRef.current)
      searchOverlayRef.current = null
    }
  }

  // ─── Submit input ref (for programmatic overlay control) ───────────────────
  const submitInputRef = useRef<EntitySearchSubmitInput>(null)

  // ─── Submit key handler ─────────────────────────────────────────────────────
  const handleSubmitKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return
      event.preventDefault()
      const committed = handleSubmitPress()
      if (committed) {
        submitInputRef.current?.open()
      }
    },
    [handleSubmitPress]
  )

  // ─── Render ─────────────────────────────────────────────────────────────────
  // iconName from component props is the canonical source; fall back to entity schema.
  const entityIconName = getWebEntityIconByName(props.iconName)
    ?? getWebEntityDisplayFromSchema(entitySchema)?.iconName

  /**
   * MW's replacement for the clear button under a selected card. Clearing is
   * still what happens — the search commits the replacement on its way back —
   * but in auto mode the overlay opens in the same tap rather than dropping
   * the user on the empty-state launcher. Submit mode has no overlay to open:
   * `handleClear` already returns to the inline input and focuses it.
   */
  function handleFindDifferent() {
    handleClear()
    if (searchMode !== 'submit') {
      openOverlay()
    }
  }

  const findDifferentSource = getFindDifferentLabel(entityDisplayName)
  const findDifferentLabel =
    translateString(findDifferentSource, (translationTable as any) ?? {}) || findDifferentSource

  function renderControl() {
    if (isMultiSelectMode) {
      const hasValues = !_.isEmpty(multiValues)
      if (hasValues) {
        const addRemoveLabel = query?.entityType
          ? `Add / remove ${entityDisplayNamePlural}`
          : 'Add / remove items'
        return (
          <div className='ui2-entitySearchField-multiSelectBody'>
            <EntitySearchMultiChips
              ref={multiChipsRef}
              values={multiValues}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              entityEditSchemaPath={entityEditSchemaPath}
              entityReadOnlySchemaPath={entityReadOnlySchemaPath}
              extraState={extraState}
              onMorePress={() => openOverlay({ showSelectedOnly: true })}
              onModalClose={() => multiChipsRef.current?.pulse()}
            />
            {!isDisabled && !isReadOnly && (
              <div className='ui2-entitySearchField-multiSelectActions'>
                <button
                  className='ui2-entitySearchField-editSelectionBtn'
                  type='button'
                  onClick={() => openOverlay()}
                >
                  <UI2Icon name={UI2IconNames.SEARCH} size={12} />
                  {addRemoveLabel}
                </button>
              </div>
            )}
          </div>
        )
      }
      // empty → fall through to auto-mode launcher
    }

    if (entity) {
      return (
        <EntitySearchSelectedCard
          ref={selectedCardRef}
          entity={entity}
          entitySchema={entitySchema}
          entityPreviewCardSchemaPath={entityPreviewCardSchemaPath}
          entityEditSchemaPath={entityEditSchemaPath}
          entityReadOnlySchemaPath={entityReadOnlySchemaPath}
          extraState={extraState}
          optionValuePath={optionValuePath}
          typeStripeIconName={props.iconName}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          isLoading={isEntityLoading}
          isValueLocked={isValueLocked}
          findDifferentLabel={findDifferentLabel}
          pulseTick={openEntityOnSelect ? 0 : pulseTick}
          isVertical={!isHorizontalLayout}
          onClear={handleClear}
          onFindDifferent={handleFindDifferent}
          onModalClose={pulseSelectedCard}
        />
      )
    }

    if (searchMode === 'submit') {
      return (
        <EntitySearchSubmitInput
          ref={submitInputRef}
          fieldProps={props}
          componentsContext={componentsContext}
          entitySchema={entitySchema}
          entityDisplayName={entityDisplayName}
          entityDisplayNamePlural={entityDisplayNamePlural}
          schemaPaths={schemaPaths}
          searchFilter={submitSearchFilter}
          requestedSearchFilter={submitRequestedSearchFilter}
          isCurrentFilterCommitted={isCurrentFilterCommitted}
          isSubmitButtonVisible={isSubmitButtonVisible}
          isSubmitResultsOpen={isSubmitResultsOpen}
          submitWrapperWidth={submitWrapperWidth}
          onSearchFilterChange={handleSubmitFilterChange}
          onSearchKeyDown={handleSubmitKeyDown}
          onSubmitPress={handleSubmitPress}
          onResultsClose={handleSubmitResultsClose}
          onResultsClosed={handleSubmitResultsClose}
          onCommitSelection={handleSubmitCommitSelection}
          onCommitRawValue={handleCommitRawValue}
          onSubmitSearch={handleSubmitSearch}
          onWrapperRef={handleSubmitWrapperRef}
        />
      )
    }

    // Auto-mode empty state button
    const translated =
      translateString(
        placeholder ?? `Find ${entityDisplayName}`,
        (translationTable as any) ?? {}
      ) || (placeholder ?? `Find ${entityDisplayName}`)

    return (
      <UI2EmptyStateLauncher
        onPress={openOverlay}
        message={translated}
        icon={entityIconName ? <UI2Icon name={entityIconName} size={16} /> : undefined}
        isDisabled={isDisabled || isEntityLoading || undefined}
      />
    )
  }

  function renderReadOnly(_value: any, { editNode }: { editNode?: React.ReactNode } = {}) {
    if (isMultiSelectMode) {
      const content = _.isEmpty(multiValues)
        ? <span className='ui2-entitySearchField-readOnlyEmpty'>No value available</span>
        : (
          <div className='ui2-entitySearchField-multiSelectBody'>
            <EntitySearchMultiChips
              ref={multiChipsRef}
              values={multiValues}
              isReadOnly={true}
              entityEditSchemaPath={entityEditSchemaPath}
              entityReadOnlySchemaPath={entityReadOnlySchemaPath}
              extraState={extraState}
              onMorePress={() => openOverlay({ showSelectedOnly: true })}
              onModalClose={() => multiChipsRef.current?.pulse()}
            />
          </div>
        )
      return (
        <>
          <div className='ui2-fieldShell-readOnly-content'>{content}</div>
          {editNode}
        </>
      )
    }

    const content = !entity
      ? <span className='ui2-entitySearchField-readOnlyEmpty'>No value available</span>
      : (
        <EntitySearchSelectedCard
          entity={entity}
          entitySchema={entitySchema}
          entityPreviewCardSchemaPath={entityPreviewCardSchemaPath}
          entityEditSchemaPath={entityEditSchemaPath}
          entityReadOnlySchemaPath={entityReadOnlySchemaPath}
          extraState={extraState}
          optionValuePath={optionValuePath}
          typeStripeIconName={props.iconName}
          isReadOnly={true}
          isLoading={false}
          isValueLocked={true}
          findDifferentLabel={''}
          pulseTick={0}
          isVertical={true}
          onClear={_.noop}
          onFindDifferent={_.noop}
        />
      )
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{content}</div>
        {editNode}
      </>
    )
  }

  return (
    <UI2FieldShell
      {...(fieldShellPassthrough as any)}
      pulseRef={fieldPulseRef}
      value={value}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      enablePrefilledMode={false}
      frames={frames as any}
      translationTable={translationTable}
      isHorizontalLayout={isHorizontalLayout}
      className={classNames('ui2-entitySearchField', (fieldShellPassthrough as any).className)}
      renderReadOnly={renderReadOnly}
    >
      <UI2PulseHighlight ref={fieldPulseRef}>
        <div ref={fieldScrollRef} style={{ width: '100%' }}>
          {renderControl()}
        </div>
      </UI2PulseHighlight>
    </UI2FieldShell>
  )
}
